import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Upload, FileSpreadsheet, CheckCircle, AlertTriangle, ArrowLeft, Loader2, UserCheck, UserPlus, Wrench, ChevronDown, ChevronUp } from 'lucide-react';

export default function AdminImportCSV() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [teams, setTeams] = useState([]);
  const [teamId, setTeamId] = useState('');
  const [opponentName, setOpponentName] = useState('');
  const [dateTime, setDateTime] = useState('');
  const [isHome, setIsHome] = useState('true');
  const [homeScore, setHomeScore] = useState('');
  const [awayScore, setAwayScore] = useState('');
  const [csvFile, setCsvFile] = useState(null);

  const [step, setStep] = useState('upload'); // upload | preview | done
  const [preview, setPreview] = useState(null);
  const [roster, setRoster] = useState([]);
  const [playerMappings, setPlayerMappings] = useState({}); // jersey_number -> player_id or null
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // ── Dev utility: backfill migration for pre-fix CSV imports ──
  const [showDevUtility, setShowDevUtility] = useState(false);
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const [backfillError, setBackfillError] = useState(null);

  const runBackfill = async (dryRun) => {
    if (backfillBusy) return;
    if (!dryRun) {
      const ok = window.confirm(
        'EXECUTE backfill against the live database?\n\n' +
        'This will stamp tracking_mode=BOTH_TEAMS on legacy CSV imports ' +
        'and synthesise missing opponent totals. The action is idempotent ' +
        'but it WRITES TO PROD. Run a Dry Run first.'
      );
      if (!ok) return;
    }
    setBackfillBusy(true);
    setBackfillError(null);
    setBackfillResult(null);
    try {
      const r = await api.post(
        `/admin/migrations/backfill-csv-imports?dry_run=${dryRun ? 'true' : 'false'}`
      );
      setBackfillResult(r.data);
    } catch (err) {
      setBackfillError(err?.response?.data?.detail || err?.message || 'Backfill failed');
    } finally {
      setBackfillBusy(false);
    }
  };

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    try {
      const res = await api.get('/admin/teams?limit=100');
      setTeams(res.data.teams || []);
    } catch {
      toast.error('Failed to load teams');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        toast.error('Please upload a CSV file');
        return;
      }
      setCsvFile(file);
    }
  };

  const handlePreview = async () => {
    if (!teamId || !csvFile) {
      toast.error('Select a team and upload a CSV file');
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      formData.append('team_id', teamId);

      const res = await api.post('/admin/games/import-csv/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPreview(res.data);
      setRoster(res.data.roster || []);

      // Initialize mappings from auto-matched results
      const mappings = {};
      (res.data.players || []).forEach(p => {
        const jn = String(p.jersey_number);
        mappings[jn] = p.matched ? p.roster_match.player_id : null;
      });
      setPlayerMappings(mappings);
      setStep('preview');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to parse CSV');
    } finally {
      setLoading(false);
    }
  };

  const handleMappingChange = (jerseyNumber, playerId) => {
    setPlayerMappings(prev => ({
      ...prev,
      [jerseyNumber]: playerId === '_new' ? null : playerId
    }));
  };

  const getConfirmedCount = () => {
    if (!preview) return 0;
    return preview.players.filter(p => playerMappings[String(p.jersey_number)] != null).length;
  };

  const handleImport = async () => {
    if (!opponentName.trim()) {
      toast.error('Opponent name is required');
      return;
    }
    if (!dateTime) {
      toast.error('Game date is required');
      return;
    }

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      formData.append('team_id', teamId);
      formData.append('opponent_name', opponentName.trim());
      formData.append('date_time', new Date(dateTime).toISOString());
      formData.append('is_home', isHome);
      formData.append('home_score', homeScore || '0');
      formData.append('away_score', awayScore || '0');

      // Send confirmed player mappings
      const mappingsList = Object.entries(playerMappings).map(([jn, pid]) => ({
        jersey_number: jn,
        player_id: pid
      }));
      formData.append('player_mappings', JSON.stringify(mappingsList));

      const res = await api.post('/admin/games/import-csv/confirm', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportResult(res.data);
      setStep('done');
      toast.success('Game imported successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const selectedTeam = teams.find(t => t.team_id === teamId);

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="admin-import-csv-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/games')} data-testid="back-to-games">
          <ArrowLeft className="w-4 h-4 mr-2" /> Games
        </Button>
        <div>
          <h1 className="text-2xl font-black uppercase tracking-tight text-slate-900" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            Import GameChanger CSV
          </h1>
          <p className="text-sm text-slate-500">Upload a single-game filtered box score export from GameChanger</p>
        </div>
      </div>

      {/* Step: Upload */}
      {step === 'upload' && (
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
            <h2 className="font-bold text-slate-900 uppercase text-sm tracking-wider">1. Select Team & Upload CSV</h2>

            <div>
              <Label className="text-sm font-bold uppercase tracking-wider text-slate-600 mb-2 block">Team *</Label>
              <Select value={teamId} onValueChange={setTeamId}>
                <SelectTrigger className="h-12" data-testid="import-team-select">
                  <SelectValue placeholder="Select team..." />
                </SelectTrigger>
                <SelectContent>
                  {teams.map(t => (
                    <SelectItem key={t.team_id} value={t.team_id}>
                      {t.name} <span className="text-slate-400 ml-1">({t.join_code || t.team_id?.slice(-6)})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-sm font-bold uppercase tracking-wider text-slate-600 mb-2 block">GameChanger CSV File *</Label>
              <div
                className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-blue-400 hover:bg-blue-50/30 transition-colors cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
                data-testid="csv-drop-zone"
              >
                {csvFile ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileSpreadsheet className="w-8 h-8 text-green-600" />
                    <div className="text-left">
                      <p className="font-semibold text-slate-900">{csvFile.name}</p>
                      <p className="text-xs text-slate-500">{(csvFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <Upload className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                    <p className="text-slate-600 font-medium">Click to select CSV file</p>
                    <p className="text-xs text-slate-400 mt-1">GameChanger box score export (.csv)</p>
                  </>
                )}
                <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={handleFileChange} />
              </div>
            </div>

            <Button
              onClick={handlePreview}
              disabled={!teamId || !csvFile || loading}
              className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-bold uppercase tracking-wider"
              data-testid="preview-csv-btn"
            >
              {loading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <FileSpreadsheet className="w-5 h-5 mr-2" />}
              {loading ? 'Parsing...' : 'Preview Import'}
            </Button>
          </div>
        </div>
      )}

      {/* Step: Preview */}
      {step === 'preview' && preview && (
        <div className="space-y-6">
          {/* Summary */}
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-slate-900 uppercase text-sm tracking-wider">2. Confirm Player Matching</h2>
              <Button variant="ghost" size="sm" onClick={() => setStep('upload')} data-testid="back-to-upload">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-black text-slate-900" data-testid="preview-player-count">{preview.summary.total_players}</p>
                <p className="text-xs text-slate-500 uppercase font-medium">Players</p>
              </div>
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-black text-green-700" data-testid="preview-matched-count">{getConfirmedCount()}</p>
                <p className="text-xs text-green-600 uppercase font-medium">Matched</p>
              </div>
              <div className={`rounded-lg p-3 text-center ${(preview.summary.total_players - getConfirmedCount()) > 0 ? 'bg-amber-50' : 'bg-slate-50'}`}>
                <p className={`text-2xl font-black ${(preview.summary.total_players - getConfirmedCount()) > 0 ? 'text-amber-700' : 'text-slate-400'}`} data-testid="preview-unmatched-count">
                  {preview.summary.total_players - getConfirmedCount()}
                </p>
                <p className={`text-xs uppercase font-medium ${(preview.summary.total_players - getConfirmedCount()) > 0 ? 'text-amber-600' : 'text-slate-400'}`}>New / Unmatched</p>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-start gap-2">
              <UserCheck className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-blue-800">
                Review each player below. Confirm the roster match or select a different player from the dropdown.
                Players marked "Create New" will have a new roster entry created.
              </p>
            </div>

            {/* Player matching table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="preview-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="px-3 py-2 text-left text-xs font-bold text-slate-500 uppercase">#</th>
                    <th className="px-3 py-2 text-left text-xs font-bold text-slate-500 uppercase">CSV Name</th>
                    <th className="px-3 py-2 text-left text-xs font-bold text-slate-500 uppercase min-w-[220px]">Roster Match</th>
                    <th className="px-3 py-2 text-center text-xs font-bold text-slate-500 uppercase">PTS</th>
                    <th className="px-3 py-2 text-center text-xs font-bold text-slate-500 uppercase">FG</th>
                    <th className="px-3 py-2 text-center text-xs font-bold text-slate-500 uppercase">3PT</th>
                    <th className="px-3 py-2 text-center text-xs font-bold text-slate-500 uppercase">FT</th>
                    <th className="px-3 py-2 text-center text-xs font-bold text-slate-500 uppercase">REB</th>
                    <th className="px-3 py-2 text-center text-xs font-bold text-slate-500 uppercase">AST</th>
                    <th className="px-3 py-2 text-center text-xs font-bold text-slate-500 uppercase">STL</th>
                    <th className="px-3 py-2 text-center text-xs font-bold text-slate-500 uppercase">BLK</th>
                    <th className="px-3 py-2 text-center text-xs font-bold text-slate-500 uppercase">TO</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {preview.players.map((p, idx) => {
                    const jn = String(p.jersey_number);
                    const currentMapping = playerMappings[jn];
                    const isMatched = currentMapping != null;

                    return (
                      <tr key={idx} className={!isMatched ? 'bg-amber-50/50' : ''} data-testid={`preview-row-${idx}`}>
                        <td className="px-3 py-2 font-bold text-slate-700">{p.jersey_number}</td>
                        <td className="px-3 py-2 font-medium text-slate-900">{p.csv_name}</td>
                        <td className="px-3 py-2">
                          <Select
                            value={currentMapping || '_new'}
                            onValueChange={(val) => handleMappingChange(jn, val)}
                          >
                            <SelectTrigger
                              className={`h-9 text-xs ${isMatched ? 'border-green-300 bg-green-50' : 'border-amber-300 bg-amber-50'}`}
                              data-testid={`mapping-select-${idx}`}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="_new">
                                <span className="flex items-center gap-1 text-amber-600">
                                  <UserPlus className="w-3 h-3" /> Create New
                                </span>
                              </SelectItem>
                              {roster.map(r => (
                                <SelectItem key={r.player_id} value={r.player_id}>
                                  <span className="flex items-center gap-1">
                                    <UserCheck className="w-3 h-3 text-green-600" />
                                    #{r.jersey_number} {r.name}
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-3 py-2 text-center font-bold">{p.stats.pts}</td>
                        <td className="px-3 py-2 text-center">{p.stats.fgm}-{p.stats.fga}</td>
                        <td className="px-3 py-2 text-center">{p.stats.three_pm}-{p.stats.three_pa}</td>
                        <td className="px-3 py-2 text-center">{p.stats.ftm}-{p.stats.fta}</td>
                        <td className="px-3 py-2 text-center">{p.stats.reb}</td>
                        <td className="px-3 py-2 text-center">{p.stats.ast}</td>
                        <td className="px-3 py-2 text-center">{p.stats.stl}</td>
                        <td className="px-3 py-2 text-center">{p.stats.blk}</td>
                        <td className="px-3 py-2 text-center">{p.stats.to}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-300 font-bold">
                    <td className="px-3 py-2" colSpan={3}>TOTALS</td>
                    <td className="px-3 py-2 text-center">{preview.summary.total_points}</td>
                    <td colSpan={8}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Game Details */}
          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
            <h2 className="font-bold text-slate-900 uppercase text-sm tracking-wider">3. Game Details</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-bold uppercase tracking-wider text-slate-600 mb-2 block">Opponent Name *</Label>
                <Input value={opponentName} onChange={(e) => setOpponentName(e.target.value)} placeholder="e.g. Chicago Flames" className="h-12" data-testid="import-opponent" />
              </div>
              <div>
                <Label className="text-sm font-bold uppercase tracking-wider text-slate-600 mb-2 block">Game Date *</Label>
                <Input type="datetime-local" value={dateTime} onChange={(e) => setDateTime(e.target.value)} className="h-12" data-testid="import-date" />
              </div>
              <div>
                <Label className="text-sm font-bold uppercase tracking-wider text-slate-600 mb-2 block">Home / Away</Label>
                <Select value={isHome} onValueChange={setIsHome}>
                  <SelectTrigger className="h-12" data-testid="import-home-away">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Home</SelectItem>
                    <SelectItem value="false">Away</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-bold uppercase tracking-wider text-slate-600 mb-2 block">
                    {isHome === 'true' ? `${selectedTeam?.name || 'Home'}` : 'Away'} Score
                  </Label>
                  <Input type="number" value={homeScore} onChange={(e) => setHomeScore(e.target.value)} placeholder="0" className="h-12" data-testid="import-home-score" />
                </div>
                <div>
                  <Label className="text-sm font-bold uppercase tracking-wider text-slate-600 mb-2 block">
                    {isHome === 'true' ? 'Opponent' : `${selectedTeam?.name || 'Away'}`} Score
                  </Label>
                  <Input type="number" value={awayScore} onChange={(e) => setAwayScore(e.target.value)} placeholder="0" className="h-12" data-testid="import-away-score" />
                </div>
              </div>
            </div>

            <Button
              onClick={handleImport}
              disabled={importing || !opponentName.trim() || !dateTime}
              className="w-full h-12 bg-green-700 hover:bg-green-800 text-white font-bold uppercase tracking-wider"
              data-testid="confirm-import-btn"
            >
              {importing ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <CheckCircle className="w-5 h-5 mr-2" />}
              {importing ? 'Importing...' : `Import Game (${preview.summary.total_players} players)`}
            </Button>
          </div>
        </div>
      )}

      {/* Step: Done */}
      {step === 'done' && importResult && (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center" data-testid="import-success">
          <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
          <h2 className="text-2xl font-black text-slate-900 uppercase mb-2">Game Imported</h2>
          <p className="text-slate-600 mb-6">{importResult.message}</p>

          <div className="grid grid-cols-3 gap-4 max-w-md mx-auto mb-8">
            <div className="bg-green-50 rounded-lg p-3">
              <p className="text-2xl font-black text-green-700">{importResult.box_scores_created}</p>
              <p className="text-xs text-green-600 uppercase font-medium">Players</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-3">
              <p className="text-2xl font-black text-blue-700">{importResult.total_points}</p>
              <p className="text-xs text-blue-600 uppercase font-medium">Total PTS</p>
            </div>
            <div className="bg-amber-50 rounded-lg p-3">
              <p className="text-2xl font-black text-amber-700">{importResult.unmatched_players?.length || 0}</p>
              <p className="text-xs text-amber-600 uppercase font-medium">New Roster</p>
            </div>
          </div>

          {importResult.unmatched_players?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-left max-w-md mx-auto">
              <p className="text-sm font-bold text-amber-800 mb-1">New roster entries created:</p>
              {importResult.unmatched_players.map((p, i) => (
                <p key={i} className="text-sm text-amber-700">#{p.jersey} {p.name}</p>
              ))}
            </div>
          )}

          <div className="flex gap-3 justify-center">
            <Button variant="outline" onClick={() => navigate(`/admin/games`)} data-testid="goto-games-btn">
              View Games
            </Button>
            <Button
              className="bg-slate-900 hover:bg-slate-800 text-white"
              onClick={() => { setStep('upload'); setCsvFile(null); setPreview(null); setImportResult(null); setOpponentName(''); setDateTime(''); setHomeScore(''); setAwayScore(''); setPlayerMappings({}); }}
              data-testid="import-another-btn"
            >
              Import Another Game
            </Button>
          </div>
        </div>
      )}

      {/* ── Dev utility ─────────────────────────────────────────────
          One-shot maintenance: backfill legacy GameChanger CSV imports
          that pre-date the tracking_mode + opponent-total fix. Dry-run
          by default. Idempotent — safe to re-run. Hidden behind a
          collapse so it doesn't clutter the normal import flow. */}
      <div className="mt-10 border-t border-slate-200 pt-8" data-testid="admin-import-dev-utility">
        <button
          type="button"
          onClick={() => setShowDevUtility((v) => !v)}
          className="w-full flex items-center justify-between text-xs font-bold uppercase tracking-[.12em] text-slate-500 hover:text-slate-900 transition-colors"
          data-testid="dev-utility-toggle"
        >
          <span className="flex items-center gap-2">
            <Wrench className="w-3.5 h-3.5" />
            Dev Utility · CSV Import Backfill
          </span>
          {showDevUtility ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {showDevUtility && (
          <div className="mt-4 bg-amber-50/40 border border-amber-200 rounded-xl p-5 space-y-4" data-testid="dev-utility-panel">
            <div className="text-sm text-slate-700 leading-relaxed">
              <p className="font-semibold text-slate-900 mb-1">
                Backfill pre-fix CSV imports
              </p>
              <p className="text-slate-600">
                Stamps <code className="font-mono bg-white border border-slate-200 px-1 rounded">tracking_mode = BOTH_TEAMS</code>{' '}
                on legacy GameChanger imports and synthesises the missing
                opponent team-total so they surface in the Stats Players
                view and team aggregate. Idempotent — safe to re-run.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => runBackfill(true)}
                disabled={backfillBusy}
                data-testid="dev-utility-dry-run"
                className="flex-1"
              >
                {backfillBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Dry Run
              </Button>
              <Button
                type="button"
                onClick={() => runBackfill(false)}
                disabled={backfillBusy}
                data-testid="dev-utility-execute"
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
              >
                {backfillBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Execute
              </Button>
            </div>

            {backfillError && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="dev-utility-error">
                <strong>Error:</strong> {backfillError}
              </div>
            )}

            {backfillResult && (
              <div
                className="rounded-lg border border-slate-200 bg-white p-3 text-sm"
                data-testid="dev-utility-result"
              >
                <div className="flex items-center gap-2 mb-2">
                  {backfillResult.dry_run ? (
                    <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-slate-100 text-slate-700">
                      Dry Run
                    </span>
                  ) : (
                    <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-100 text-emerald-800">
                      Applied
                    </span>
                  )}
                  <span className="text-xs text-slate-500">{new Date().toLocaleTimeString()}</span>
                </div>
                <dl className="grid grid-cols-2 gap-y-1 gap-x-4 text-xs">
                  {backfillResult.dry_run ? (
                    <>
                      <dt className="text-slate-500">Imported games found</dt>
                      <dd className="font-mono font-bold text-slate-900">{backfillResult.imported_games_total ?? 0}</dd>
                      <dt className="text-slate-500">Needing tracking_mode</dt>
                      <dd className="font-mono font-bold text-slate-900">{backfillResult.games_needing_tracking_mode ?? 0}</dd>
                      <dt className="text-slate-500">Needing opponent total</dt>
                      <dd className="font-mono font-bold text-slate-900">{backfillResult.games_needing_opponent_total ?? 0}</dd>
                    </>
                  ) : (
                    <>
                      <dt className="text-slate-500">tracking_mode updated</dt>
                      <dd className="font-mono font-bold text-emerald-700">{backfillResult.tracking_mode_updated ?? 0}</dd>
                      <dt className="text-slate-500">Opponent totals inserted</dt>
                      <dd className="font-mono font-bold text-emerald-700">{backfillResult.opponent_totals_inserted ?? 0}</dd>
                    </>
                  )}
                </dl>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
